import React, {
  Fragment,
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";

import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { NotificationTargetSetting } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { getUserNotificationSettings } from "api/endpoints/notifications";
import { callOptionsHeaders } from "api/helpers";
import { SnackbarContext } from "context/snackbar-context";
import { UserUrlParamTypes } from "types/user-display";

type NotificationTargets = {
  [target: string]: {
    enabled: boolean;
    value: boolean;
    editable: boolean;
  };
};

type NotificationItems = {
  [item: string]: {
    label: string;
    targets: NotificationTargets;
  };
};

type NotificationSettings = {
  [section: string]: {
    label: string;
    items: NotificationItems;
  };
};

enum NotificationTarget {
  EMAIL = "switch_email",
  PUSH = "switch_push",
  SMS = "switch_sms",
}

type ChannelStatus = {
  [targetId: string]: { enabled: boolean; value: boolean };
};

type UserNotificationsTabProps = {
  orgId?: string;
};

const SMS_NO_MOBILE_NUMBER_TOOLTIP = "No mobile number has been added";

const UserNotificationsTab: FunctionComponent<UserNotificationsTabProps> = ({
  orgId,
}) => {
  const { regionCode, userId } = useParams<UserUrlParamTypes>();
  const [userNotificationSettings, setUserNotificationSettings] =
    useState<NotificationSettings>({});
  const [channelStatus, setChannelStatus] = useState<ChannelStatus>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { showError } = useContext(SnackbarContext);

  const stripPrefixFromID = (id: string) => {
    return id.replace(/^(section|switch)_(email|push|sms)_/, "");
  };

  const parseNotificationSettings = (targets: NotificationTargetSetting[]) => {
    const settings: NotificationSettings = {};
    const channels: ChannelStatus = {};
    for (const target of targets) {
      channels[target.id] = { enabled: target.enabled, value: target.value };

      for (const section of target.sections) {
        const sectionID = stripPrefixFromID(section.id);
        if (!(sectionID in settings)) {
          settings[stripPrefixFromID(section.id)] = {
            label: section.label,
            items: {},
          };
        }

        for (const item of section.items) {
          const itemID = stripPrefixFromID(item.id);
          if (!(itemID in settings[sectionID].items)) {
            settings[sectionID].items[itemID] = {
              label: item.label,
              targets: {},
            };
          }

          settings[sectionID].items[itemID].targets[target.id] = {
            enabled: item.enabled,
            value: item.value,
            editable: item.editable,
          };
        }
      }
    }

    return { settings, channels };
  };

  const loadUserNotificationSettings = () => {
    if (!orgId) {
      console.warn("orgId is required to load user notification settings");
      return;
    }

    setIsLoading(true);

    getUserNotificationSettings(
      { userId, orgId },
      callOptionsHeaders(regionCode, userId)
    )
      .then((resp) => {
        const { settings, channels } = parseNotificationSettings(resp.targets);
        setUserNotificationSettings(settings);
        setChannelStatus(channels);
      })
      .catch(showError)
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    loadUserNotificationSettings();
    // Load once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isItemEditable = (targets: NotificationTargets): boolean => {
    return Object.values(targets).every((t) => t.editable);
  };

  const displayStatusIcon = (
    targets: NotificationTargets,
    targetName: NotificationTarget
  ) => {
    if (!(targetName in targets)) {
      return <></>;
    }

    const target = targets[targetName];
    if (target.value) {
      return (
        <CheckCircleIcon color={target.enabled ? "primary" : "disabled"} />
      );
    }
    return <CancelIcon color={target.enabled ? "error" : "disabled"} />;
  };

  const renderChannelChip = (value: boolean) => (
    <Chip
      label={value ? "On" : "Off"}
      color={value ? "success" : "default"}
      size="small"
      sx={{ ml: 1 }}
    />
  );

  const renderSmsChip = () => {
    if (!(NotificationTarget.SMS in channelStatus)) {
      return null;
    }
    const sms = channelStatus[NotificationTarget.SMS];
    if (!sms.enabled) {
      return (
        <Tooltip title={SMS_NO_MOBILE_NUMBER_TOOLTIP}>
          <Chip
            label={sms.value ? "On" : "Off"}
            color="warning"
            size="small"
            sx={{ ml: 1 }}
          />
        </Tooltip>
      );
    }
    return renderChannelChip(sms.value);
  };

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold" }}>Category</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Notification type</TableCell>
            {/* Push and Email channels do not have an equivalent "enabled but blocked" state — only SMS can be enabled=false due to a missing mobile number */}
            <TableCell sx={{ fontWeight: "bold" }} align="center">
              Push
              {NotificationTarget.PUSH in channelStatus &&
                renderChannelChip(channelStatus[NotificationTarget.PUSH].value)}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="center">
              Email
              {NotificationTarget.EMAIL in channelStatus &&
                renderChannelChip(
                  channelStatus[NotificationTarget.EMAIL].value
                )}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="center">
              SMS
              {renderSmsChip()}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="center">
              Editable
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={12}>
                <LinearProgress />
              </TableCell>
            </TableRow>
          ) : (
            Object.values(userNotificationSettings).map((section) => (
              <Fragment key={section.label}>
                <TableRow>
                  <TableCell
                    sx={{ fontWeight: "bold" }}
                    rowSpan={Object.keys(section.items).length + 1}
                  >
                    {section.label}
                  </TableCell>
                </TableRow>
                {Object.values(section.items).map((item) => (
                  <TableRow key={item.label}>
                    <TableCell>{item.label}</TableCell>
                    <TableCell align="center" data-testid="push">
                      {displayStatusIcon(item.targets, NotificationTarget.PUSH)}
                    </TableCell>
                    <TableCell align="center" data-testid="email">
                      {displayStatusIcon(
                        item.targets,
                        NotificationTarget.EMAIL
                      )}
                    </TableCell>
                    <TableCell align="center" data-testid="sms">
                      {displayStatusIcon(item.targets, NotificationTarget.SMS)}
                    </TableCell>
                    <TableCell align="center" data-testid="editable">
                      {isItemEditable(item.targets) ? (
                        <CheckCircleIcon color="primary" />
                      ) : (
                        <CancelIcon color="error" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </Fragment>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default UserNotificationsTab;
